import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { disabled, required, RootFieldContext, schema, validate } from '@angular/forms/signals';
import { AddressService } from './address.service';
import { Address } from '@app/models/addresses/address.interface';
import { ChromiumAddressFormat } from '@app/dtos/addresses/chromium-address-format.dto';
import { AddressLine } from '@app/models/addresses/formatting/address-line.class';
import { TextAddressFieldInfo } from '@app/models/addresses/formatting/text-address-field-info.class';
import { DropdownAddressFieldInfo } from '@app/models/addresses/formatting/dropdown-address-field-info.class';
import { AddressFieldInfo } from '@app/models/addresses/formatting/address-field-info.class';


export const fieldMap = new Map<string, string>([
    ['A', 'street'],
    ['D', 'dependentLocality'], // hidden label, not used
    ['C', 'locality'],
    ['S', 'region'],
    ['Z', 'postalCode']
]);
const autocompleteMap = new Map<string, string>([
    ['A', 'address-line1'],
    ['D', 'address-line2'],
    ['S', 'address-level1'],
    ['C', 'address-level2'],
    ['Z', 'postal-code']
]);

@Injectable({
    providedIn: 'root'
})
export class AddressSchemaService {

    private readonly addressService = inject(AddressService);
    readonly country = signal('');
    private readonly addressFormatResource = this.addressService.addressFormatResource(this.country);

    readonly isLoading = this.addressFormatResource.isLoading;
    readonly hasError = this.addressFormatResource.error;

    readonly fields = computed(() => {
        return this.addressFormat()?.flatMap(x => x.fields);
    });

    readonly addressFormat = linkedSignal<AddressLine[], AddressLine[]>({
        source: computed(() => {
            const addressFormat = [] as AddressLine[];
            if (!this.addressFormatResource.hasValue()) {
                return;
            }
            const format = this.addressFormatResource.value();
            const lines = format.fmt.split('%n');
            for (let line of lines) {
                const codes = Array.from(
                    new Set(line.match(/%[A-Z]/g)?.map((f) => f[1]).filter((c) => c !== 'N' && c !== 'O') ?? [])
                );
                const addressLine = this.generateFormRow(codes, format);
                if (addressLine.fields.length > 0) {
                    addressFormat.push(addressLine);
                }
            }
            return addressFormat;
        }),
        computation: (source, previous) => source ?? previous?.value ?? []
    });

    readonly addressSchema = schema<Address>((schemaPath) => {
        required(schemaPath.countryCode, { message: `addressing.requiredField` });
        for (const key of fieldMap.values()) {
            disabled(schemaPath[key], () => !this.addressFormatResource.hasValue() || !this.fields()?.some(f => f.id === key));
            required(schemaPath[key], { when: () => this.requiredFields().includes(key), message: `addressing.requiredField` });
            validate(schemaPath[key], (value) => {
                const field = this.fields()?.find(f => f.id === key);
                if (field?.validationFunction) {
                    return field.validationFunction(value as RootFieldContext<string>);
                }
                return null;
            });
        }
    });

    private readonly validRegions = computed(() => {
        const format = this.addressFormatResource.value();
        if (!format?.sub_isoids) {
            return new Map<string, string>();
        }
        const isoIds = format.sub_isoids.split('~');
        const names = format.sub_names?.split('~') ?? isoIds;
        return new Map<string, string>(
            isoIds.map((id, index) => [id, names[index]])
        );
    });

    private readonly requiredFields = computed(() => {
        if (!this.addressFormatResource.hasValue()) {
            return [];
        }
        return this.addressFormatResource.value().require?.match(/[A-Z]/g).filter(x => x && x !== 'O' && x !== 'N').map(x => fieldMap.get(x)) ?? [];
    });

    private generateFormRow(codes: string[], format: ChromiumAddressFormat) {
        const addressLine = new AddressLine();
        for (let code of codes) {
            let fieldInfo: AddressFieldInfo;
            if (code === 'S' && format.sub_isoids) {
                const dropdownFieldInfo = new DropdownAddressFieldInfo();
                const stateOptions = [...this.validRegions().entries()].map(([value, label]) => ({ value, label }));
                dropdownFieldInfo.options = [{ value: '', label: '' }, ...stateOptions];
                fieldInfo = dropdownFieldInfo;
            } else {
                fieldInfo = new TextAddressFieldInfo();
            }
            fieldInfo.id = fieldMap.get(code);
            fieldInfo.showLabel = code !== 'D'; // Do not show label for street address line 2 / dependent locality
            const translationKey = (() => {
                switch (code) {
                    case 'Z':
                        return `.${format.zip_name_type}`;
                    case 'S':
                        return `.${format.state_name_type}`;
                    default:
                        return '';
                }
            })();
            fieldInfo.label = `addressing.${fieldMap.get(code)}${translationKey}`;
            fieldInfo.autocomplete = autocompleteMap.get(code) ?? 'off';
            fieldInfo.required = this.requiredFields().includes(fieldInfo.id);
            if (code === 'Z' && format.zip) {
                fieldInfo.validationFunction = ({ value }) => {
                    const zipRegex = new RegExp(`^(?:${format.zip})?$`);
                    return zipRegex.test(value()) ? null : { kind: 'pattern', message: `addressing.invalidPostalCode.${format.zip_name_type}`, example: format.zipex };
                };
            }
            if (code === 'S' && format.sub_isoids) {
                fieldInfo.validationFunction = (({ value }) => {
                    if (!value()) {
                        return null;
                    }
                    const addressFormatResource = this.addressFormatResource.value();
                    return this.validRegions().has(value()) ? null : { kind: 'pattern', message: `addressing.invalidRegion.${addressFormatResource.state_name_type}` };
                });
            }
            addressLine.fields.push(fieldInfo);
        }
        return addressLine;
    }
}
